import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, CATEGORIES, EXPENSE_PAYMENT_MODES, PLATFORMS, suggestFromMerchant } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import StatusPill from "@/components/StatusPill";
import HelpHint from "@/components/HelpHint";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Receipt } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import EmptyState from "@/components/EmptyState";
import AutoCategorizeButton from "@/components/AutoCategorizeButton";
import CleanMerchantNamesButton from "@/components/CleanMerchantNamesButton";
import PrintPageButton from "@/components/PrintPageButton";
import HouseholdSplitBlock from "@/components/HouseholdSplitBlock";

function ExpenseForm({ cards, initial, onSaved }) {
    const [f, setF] = useState(
        initial || {
            date: new Date().toISOString().slice(0, 10),
            card_id: cards[0]?.id || "",
            transaction_type: "Debit",
            merchant: "",
            amount: 0,
            gross_amount: "",
            platform: "",
            currency: "INR",
            category: "Other",
            sub_category: "",
            business_personal: "Business",
            department: "",
            project: "",
            gst_applicable: false,
            gst_number: "",
            invoice_number: "",
            invoice_date: "",
            taxable_amount: "",
            gst_amount: "",
            itc_eligible: false,
            payment_mode: "Card Swipe",
            is_emi: false,
            emi_tenure: "",
            emi_monthly: "",
            remarks: "",
            approval_status: "Pending",
            cashback_received: false,
            cashback_amount: "",
            // Adjustments / rewards / wallet usage
            coins_used: "",
            points_used: "",
            rewards_used: "",
            wallet_balance_used: "",
            gift_card_used: "",
            cashback_used: "",
            promo_discount: "",
            platform_discount: "",
            bank_discount: "",
            adjustment_remarks: "",
            // Household · Ownership · Split (silently null in Personal workspace)
            ownership: "Personal",
            visibility: "Shared Summary",
            paid_by_user_id: "",
            belongs_to_user_ids: [],
            split: [],
        },
    );
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [autoHint, setAutoHint] = useState(null); // {applied: bool, hint: {...}}
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    // Merchant-based auto-suggest — only fills fields still at their defaults.
    // Editing the form manually after that never gets overwritten.
    const onMerchantChange = (e) => {
        const val = e.target.value;
        setF((s) => {
            const next = { ...s, merchant: val };
            const hint = suggestFromMerchant(val);
            if (!hint) {
                setAutoHint(null);
                return next;
            }
            const isDefault = {
                platform: !s.platform,
                category: !s.category || s.category === "Other",
                payment_mode: !s.payment_mode || s.payment_mode === "Card Swipe",
                business_personal: !s.business_personal || s.business_personal === "Business",
            };
            let touched = false;
            if (isDefault.platform)          { next.platform = hint.platform; touched = true; }
            if (isDefault.category)          { next.category = hint.category; touched = true; }
            if (isDefault.payment_mode)      { next.payment_mode = hint.payment_mode; touched = true; }
            if (isDefault.business_personal) { next.business_personal = hint.business_personal; touched = true; }
            setAutoHint(touched ? { hint, applied: true } : null);
            return next;
        });
    };

    // Live adjustments math — sum of everything that REDUCES the card charge
    const ADJ_FIELDS = ["coins_used", "points_used", "rewards_used", "wallet_balance_used",
                        "gift_card_used", "cashback_used", "promo_discount",
                        "platform_discount", "bank_discount"];
    const adjTotal = ADJ_FIELDS.reduce((s, k) => s + Number(f[k] || 0), 0);
    const grossNum = Number(f.gross_amount || 0);
    const cardChargeCalc = Math.max(0, grossNum - adjTotal);
    const hasAdjustments = adjTotal > 0 || grossNum > 0;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const numOrNull = (v) => (v === "" || v === undefined || v === null ? null : Number(v));
            const numOrZero = (v) => (v === "" || v === undefined || v === null ? 0 : Number(v));
            const payload = { ...f, amount: Number(f.amount || 0) };
            // Gross + adjustments
            payload.gross_amount = numOrNull(f.gross_amount);
            payload.coins_used = numOrZero(f.coins_used);
            payload.points_used = numOrZero(f.points_used);
            payload.rewards_used = numOrZero(f.rewards_used);
            payload.wallet_balance_used = numOrZero(f.wallet_balance_used);
            payload.gift_card_used = numOrZero(f.gift_card_used);
            payload.cashback_used = numOrZero(f.cashback_used);
            payload.promo_discount = numOrZero(f.promo_discount);
            payload.platform_discount = numOrZero(f.platform_discount);
            payload.bank_discount = numOrZero(f.bank_discount);
            if (!payload.platform) delete payload.platform;
            if (f.gst_applicable) {
                payload.taxable_amount = f.taxable_amount === "" ? null : Number(f.taxable_amount);
                payload.gst_amount = f.gst_amount === "" ? null : Number(f.gst_amount);
            } else {
                payload.taxable_amount = null;
                payload.gst_amount = null;
                payload.itc_eligible = false;
            }
            if (f.is_emi) {
                payload.emi_tenure = Number(f.emi_tenure) || null;
                payload.emi_monthly = Number(f.emi_monthly) || null;
            } else {
                payload.emi_tenure = null;
                payload.emi_monthly = null;
            }
            payload.cashback_amount = f.cashback_received ? Number(f.cashback_amount || 0) : 0;
            let res;
            if (initial?.id) {
                res = await api.patch(`/expenses/${initial.id}`, payload);
            } else {
                res = await api.post("/expenses", payload);
            }
            // Optional receipt upload — save as file url on server-side by proxying via statement upload style (skipped for phase 1)
            toast.success("Expense saved");
            if (res?.data?._duplicate_warning) {
                toast.warning(res.data._duplicate_warning, { duration: 8000 });
            }
            if (res?.data?._warning) {
                toast.warning(res.data._warning, { duration: 6000 });
            }
            onSaved?.(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="expense-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label className="eyebrow">Date</Label>
                    <Input type="date" value={f.date} onChange={setInput("date")} required className="mt-1.5" data-testid="ef-date" />
                </div>
                <div>
                    <Label className="eyebrow">Card</Label>
                    <Select value={f.card_id} onValueChange={set("card_id")}>
                        <SelectTrigger className="mt-1.5" data-testid="ef-card"><SelectValue placeholder="Select card" /></SelectTrigger>
                        <SelectContent>
                            {cards.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.bank_name} · {c.card_name} · •••• {c.last_4}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="eyebrow">Merchant / Vendor</Label>
                    <Input value={f.merchant} onChange={onMerchantChange} required className="mt-1.5" data-testid="ef-merchant" />
                    {autoHint?.applied && (
                        <div className="text-[11px] text-blue-700 mt-1.5 flex items-center gap-1.5" data-testid="ef-auto-hint">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
                            Auto-set: {autoHint.hint.platform} · {autoHint.hint.category} · {autoHint.hint.payment_mode} · {autoHint.hint.business_personal}
                        </div>
                    )}
                </div>
                <div>
                    <Label className="eyebrow">Amount</Label>
                    <Input type="number" step="0.01" value={f.amount} onChange={setInput("amount")} required className="mt-1.5" data-testid="ef-amount" />
                </div>
                <div>
                    <Label className="eyebrow">Category</Label>
                    <Select value={f.category} onValueChange={set("category")}>
                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-64">
                            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="eyebrow">Sub Category</Label>
                    <Input value={f.sub_category} onChange={setInput("sub_category")} className="mt-1.5" />
                </div>
                <div>
                    <Label className="eyebrow">Business / Personal</Label>
                    <Select value={f.business_personal} onValueChange={set("business_personal")}>
                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Business">Business</SelectItem>
                            <SelectItem value="Personal">Personal</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="eyebrow">Transaction type</Label>
                    <Select value={f.transaction_type || "Debit"} onValueChange={set("transaction_type")}>
                        <SelectTrigger className="mt-1.5" data-testid="ef-txn-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Debit">Debit (spend)</SelectItem>
                            <SelectItem value="Credit">Credit (refund / reversal)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="eyebrow">Payment Mode</Label>
                    <Select value={f.payment_mode} onValueChange={set("payment_mode")}>
                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {EXPENSE_PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="eyebrow">Platform / Portal</Label>
                    <Select value={f.platform || "none"} onValueChange={(v) => set("platform")(v === "none" ? "" : v)}>
                        <SelectTrigger className="mt-1.5" data-testid="ef-platform"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="max-h-64">
                            <SelectItem value="none">—</SelectItem>
                            {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="eyebrow">Department / Person</Label>
                    <Input value={f.department} onChange={setInput("department")} className="mt-1.5" />
                </div>
                <div>
                    <Label className="eyebrow">Client / Project</Label>
                    <Input value={f.project} onChange={setInput("project")} className="mt-1.5" />
                </div>
                <div>
                    <Label className="eyebrow">Invoice / Bill Number</Label>
                    <Input value={f.invoice_number} onChange={setInput("invoice_number")} className="mt-1.5" />
                </div>
                <div>
                    <Label className="eyebrow">Vendor GST</Label>
                    <Input value={f.gst_number} onChange={setInput("gst_number")} className="mt-1.5" />
                </div>
                <div className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2.5">
                    <div className="text-sm">GST Applicable</div>
                    <Switch checked={f.gst_applicable} onCheckedChange={set("gst_applicable")} />
                </div>
                {f.gst_applicable && (
                    <>
                        <div>
                            <Label className="eyebrow">Invoice Date</Label>
                            <Input type="date" value={f.invoice_date} onChange={setInput("invoice_date")} className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="eyebrow">Taxable Amount</Label>
                            <Input type="number" step="0.01" value={f.taxable_amount} onChange={setInput("taxable_amount")} className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="eyebrow">GST Amount</Label>
                            <Input type="number" step="0.01" value={f.gst_amount} onChange={setInput("gst_amount")} className="mt-1.5" />
                        </div>
                        <div className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2.5">
                            <div className="text-sm">Input Tax Credit Eligible</div>
                            <Switch checked={f.itc_eligible} onCheckedChange={set("itc_eligible")} />
                        </div>
                    </>
                )}
                <div className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2.5">
                    <div className="text-sm">EMI Transaction</div>
                    <Switch checked={f.is_emi} onCheckedChange={set("is_emi")} />
                </div>
                {f.is_emi && (
                    <>
                        <div>
                            <Label className="eyebrow">EMI Tenure (months)</Label>
                            <Input type="number" value={f.emi_tenure} onChange={setInput("emi_tenure")} className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="eyebrow">EMI Monthly Amount</Label>
                            <Input type="number" value={f.emi_monthly} onChange={setInput("emi_monthly")} className="mt-1.5" />
                        </div>
                    </>
                )}
                <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50/50 rounded-md px-3 py-2.5 sm:col-span-2">
                    <div className="text-sm text-emerald-800">Cashback received on this spend?</div>
                    <Switch checked={f.cashback_received} onCheckedChange={set("cashback_received")} data-testid="ef-cashback-toggle" />
                </div>
                {f.cashback_received && (
                    <div className="sm:col-span-2">
                        <Label className="eyebrow">Cashback Amount</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={f.cashback_amount}
                            onChange={setInput("cashback_amount")}
                            className="mt-1.5 font-mono-figure"
                            placeholder="0.00"
                            data-testid="ef-cashback-amount"
                        />
                        <div className="text-[11px] text-emerald-700 mt-1">
                            Rolls up to your yearly cashback savings on the dashboard.
                        </div>
                    </div>
                )}
            </div>

            {/* ── Adjustments / Rewards / Wallet Usage ─────────────────── */}
            <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-4" data-testid="ef-adjustments-block">
                <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-stone-500">Adjustments · Rewards · Wallet</div>
                        <div className="text-sm text-stone-700 mt-0.5">
                            Record coins, points, cashback, gift cards or discounts used on this purchase.
                        </div>
                    </div>
                    {hasAdjustments && (
                        <div className="text-right font-mono-figure">
                            <div className="text-[11px] uppercase tracking-widest text-stone-500 inline-flex items-center">
                                Charged to card
                                <HelpHint text="Final amount that actually hits the credit card. This is the number that affects card outstanding." />
                            </div>
                            <div className="text-lg font-medium text-stone-900" data-testid="ef-card-charge-preview">
                                {fmtMoney(cardChargeCalc)}
                            </div>
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                        <Label className="eyebrow">Gross Purchase Amount</Label>
                        <Input type="number" step="0.01" value={f.gross_amount} onChange={setInput("gross_amount")} placeholder="0" className="mt-1.5" data-testid="ef-gross" />
                    </div>
                    <div>
                        <Label className="eyebrow">Coins Used</Label>
                        <Input type="number" step="0.01" value={f.coins_used} onChange={setInput("coins_used")} placeholder="0" className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="eyebrow">Points Used</Label>
                        <Input type="number" step="0.01" value={f.points_used} onChange={setInput("points_used")} placeholder="0" className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="eyebrow">Rewards Used</Label>
                        <Input type="number" step="0.01" value={f.rewards_used} onChange={setInput("rewards_used")} placeholder="0" className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="eyebrow">Wallet Balance Used</Label>
                        <Input type="number" step="0.01" value={f.wallet_balance_used} onChange={setInput("wallet_balance_used")} placeholder="0" className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="eyebrow">Gift Card Used</Label>
                        <Input type="number" step="0.01" value={f.gift_card_used} onChange={setInput("gift_card_used")} placeholder="0" className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="eyebrow">Cashback Used</Label>
                        <Input type="number" step="0.01" value={f.cashback_used} onChange={setInput("cashback_used")} placeholder="0" className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="eyebrow">Promo / Coupon</Label>
                        <Input type="number" step="0.01" value={f.promo_discount} onChange={setInput("promo_discount")} placeholder="0" className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="eyebrow">Platform Discount</Label>
                        <Input type="number" step="0.01" value={f.platform_discount} onChange={setInput("platform_discount")} placeholder="0" className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="eyebrow">Bank Discount</Label>
                        <Input type="number" step="0.01" value={f.bank_discount} onChange={setInput("bank_discount")} placeholder="0" className="mt-1.5" />
                    </div>
                    <div className="col-span-2 sm:col-span-3">
                        <Label className="eyebrow">Adjustment Notes</Label>
                        <Input value={f.adjustment_remarks} onChange={setInput("adjustment_remarks")} placeholder="e.g. Flipkart SuperCoins + Bank offer" className="mt-1.5" />
                    </div>
                </div>
                {hasAdjustments && (
                    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-xs text-stone-600 font-mono-figure" data-testid="ef-adjustments-summary">
                        <span>Gross <strong className="text-stone-900">{fmtMoney(grossNum)}</strong></span>
                        <span className="text-stone-400">−</span>
                        <span>Adjustments <strong className="text-stone-900">{fmtMoney(adjTotal)}</strong></span>
                        <span className="text-stone-400">=</span>
                        <span>Charged to card <strong className="text-emerald-700">{fmtMoney(cardChargeCalc)}</strong></span>
                        {Math.abs(cardChargeCalc - Number(f.amount || 0)) > 0.01 && (
                            <button
                                type="button"
                                onClick={() => set("amount")(cardChargeCalc.toFixed(2))}
                                className="ml-auto text-blue-600 hover:text-blue-700 underline"
                                data-testid="ef-apply-card-charge"
                            >
                                Apply to Amount field
                            </button>
                        )}
                    </div>
                )}
            </div>

            <HouseholdSplitBlock
                value={{
                    ownership: f.ownership, visibility: f.visibility,
                    paid_by_user_id: f.paid_by_user_id,
                    belongs_to_user_ids: f.belongs_to_user_ids,
                    split: f.split,
                }}
                onChange={(next) => setF(s => ({ ...s, ...next }))}
            />
            <div>
                <Label className="eyebrow">Remarks</Label>
                <Textarea rows={2} value={f.remarks} onChange={setInput("remarks")} className="mt-1.5" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <Button type="submit" disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="ef-submit">
                    {busy ? "Saving…" : "Save expense"}
                </Button>
            </div>
        </form>
    );
}

export default function Expenses() {
    const { hasRole } = useAuth();
    const [cards, setCards] = useState([]);
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [cardFilter, setCardFilter] = useState("all");
    const [monthFilter, setMonthFilter] = useState("");
    const [bpFilter, setBpFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(() => {
        const params = {};
        if (cardFilter !== "all") params.card_id = cardFilter;
        if (monthFilter) params.statement_month = monthFilter;
        if (bpFilter !== "all") params.business_personal = bpFilter;
        api.get("/expenses", { params }).then((r) => setRows(r.data));
    }, [cardFilter, monthFilter, bpFilter]);

    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
    }, []);
    useEffect(() => { load(); }, [load]);

    const del = async (id) => {
        if (!window.confirm("Delete this expense? This cannot be undone.")) return;
        try {
            await api.delete(`/expenses/${id}`);
            toast.success("Expense deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to delete");
        }
    };

    const filtered = useMemo(() => {
        if (!q) return rows;
        const needle = q.toLowerCase();
        return rows.filter(
            (r) =>
                r.merchant?.toLowerCase().includes(needle) ||
                r.category?.toLowerCase().includes(needle) ||
                r.remarks?.toLowerCase().includes(needle),
        );
    }, [rows, q]);

    const cardMap = useMemo(() => Object.fromEntries(cards.map((c) => [c.id, c])), [cards]);
    const total = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);

    return (
        <div data-testid="expenses-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Expenses</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Every rupee, logged.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3">
                        {filtered.length} entries · {fmtMoney(total)} total
                    </p>
                </div>
                {hasRole("admin", "finance", "expense_user") && (
                    <div className="flex items-center gap-2">
                        <PrintPageButton />
                        <CleanMerchantNamesButton onDone={load} />
                        <AutoCategorizeButton onDone={load} />
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-expense-btn">
                                <Plus size={16} className="mr-1.5" /> Post expense
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-display text-3xl">
                                    {editing ? "Edit expense" : "New expense"}
                                </DialogTitle>
                            </DialogHeader>
                            <ExpenseForm cards={cards} initial={editing} onSaved={() => { setOpen(false); setEditing(null); load(); }} />
                        </DialogContent>
                    </Dialog>
                    </div>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                        placeholder="Search merchant, category, remarks…"
                        className="pl-9"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="expenses-search"
                    />
                </div>
                <Select value={cardFilter} onValueChange={setCardFilter}>
                    <SelectTrigger className="w-[220px]" data-testid="expenses-card-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All cards</SelectItem>
                        {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Input type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="w-[180px]" data-testid="expenses-month-filter" />
                <Select value={bpFilter} onValueChange={setBpFilter}>
                    <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="Business">Business</SelectItem>
                        <SelectItem value="Personal">Personal</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Merchant</th>
                                <th>Card</th>
                                <th>Category</th>
                                <th>B / P</th>
                                <th className="text-right">Amount</th>
                                <th>Recon</th>
                                <th>Approval</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((e) => {
                                const c = cardMap[e.card_id];
                                return (
                                    <tr key={e.id} data-testid={`expense-row-${e.id}`}>
                                        <td className="tabular-nums">{fmtDate(e.date)}</td>
                                        <td>
                                            <div className="font-medium text-stone-900">{e.merchant}</div>
                                            {e.remarks && <div className="text-[11px] text-stone-500 truncate max-w-[240px]">{e.remarks}</div>}
                                        </td>
                                        <td className="font-mono-figure">
                                            {c ? `${c.bank_name} •••• ${c.last_4}` : "—"}
                                        </td>
                                        <td>{e.category}</td>
                                        <td><span className={`text-xs ${e.business_personal === "Business" ? "text-stone-900" : "text-stone-500"}`}>{e.business_personal}</span></td>
                                        <td className="text-right tabular-nums font-medium">
                                            {e.transaction_type === "Credit" ? (
                                                <span className="text-emerald-700">+ {fmtMoney(e.amount, e.currency)}</span>
                                            ) : (
                                                fmtMoney(e.amount, e.currency)
                                            )}
                                            {e.transaction_type === "Credit" && (
                                                <div className="text-[10px] uppercase tracking-widest text-emerald-600 mt-0.5">Credit</div>
                                            )}
                                        </td>
                                        <td><span className="text-xs text-stone-600">{e.reconciliation_status}</span></td>
                                        <td>
                                            <span className={`text-xs ${e.approval_status === "Approved" ? "text-emerald-700" : e.approval_status === "Rejected" ? "text-red-700" : "text-amber-700"}`}>
                                                {e.approval_status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => { setEditing(e); setOpen(true); }} title="Edit" className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900" data-testid={`expense-edit-${e.id}`}>
                                                    <Pencil size={13} />
                                                </button>
                                                {hasRole("admin", "finance") && (
                                                    <button onClick={() => del(e.id)} title="Delete" className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600" data-testid={`expense-delete-${e.id}`}>
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr><td colSpan={9} className="p-0"><EmptyState
                                    icon={Receipt}
                                    title="No expenses recorded this month."
                                    hint="Add an expense from the top-right, or use ⌘K → Quick Entry to log one in seconds."
                                    cta={{ to: "/quick-entry", label: "Add expense", testId: "empty-expenses-cta" }}
                                    className="rounded-none border-0 py-12"
                                /></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
